import { ICONS } from '@ui/icons/types';
import { TReactSelectOption } from '@ui/select/react-select/custom-react-select.types';
import { Dispatch, SetStateAction, useRef, useState, useEffect } from 'react';
import UploadNewFont from './theme-pack-new-font-upload';
import Button from '@ui/buttons/button/button';
import Popover from '@ui/popover/popover';
import RotatableIcon from '@ui/RotatableIcon/rotatable-icon';
import Text from '@ui/text/text/text';
import Container from '@ui/container/container';

interface AddNewFontSelectProps {
  options: TReactSelectOption<string, string, { url: string; name: string }>[];
  setSelectedFont: Dispatch<
    SetStateAction<TReactSelectOption<string, string, { url: string; name: string }> | undefined>
  >;
  selectedFont?: TReactSelectOption<string, string, { url: string; name: string }>;
  placeholder?: string;
  isUploadingFont?: (uploading: boolean) => void;
}

const AddNewFontSelect = ({
  setSelectedFont,
  selectedFont,
  options,
  placeholder = '',
  isUploadingFont,
}: AddNewFontSelectProps) => {
  const wrapperRef = useRef<HTMLDivElement>(null);

  const [uploadingFont, setUploadingFont] = useState(false);
  const [isSelectOpen, setIsSelectOpen] = useState<boolean | undefined>(false);

  useEffect(() => {
    isUploadingFont?.(uploadingFont);
  }, [isUploadingFont, uploadingFont]);

  const onUploadStart = () => {
    setUploadingFont(true);
  };

  const afterUpload = (option: TReactSelectOption<string, string, { url: string; name: string }>) => {
    setUploadingFont(false);
    setSelectedFont(option);
    setIsSelectOpen(false);
  };

  const handleFontSelected = (option: TReactSelectOption<string, string, { url: string; name: string }>) => {
    setSelectedFont(option);
    setIsSelectOpen(false);
  };

  return (
    <div ref={wrapperRef} style={{ marginBottom: '20px' }}>
      <Popover
        cycleWithArrowKeys
        width="220px"
        renderTriggerButton={({ open, closing, setOpen }) => {
          return (
            <Button
              className="P2 flex jc-space-between ai-center padding-v-0 padding-h-12"
              backgroundColor="transparent"
              border={open && !closing ? '1px solid var(--gray-200)' : '1px solid var(--gray-600)'}
              hoverBorder="1px solid var(--gray-200)"
              width="220px"
              height="32px"
              onClick={() => {
                setIsSelectOpen(undefined);
                setOpen();
              }}
            >
              <Text className="P2" clamp={1}>
                {selectedFont?.label || placeholder}{' '}
              </Text>
              <RotatableIcon name={ICONS.KEYBOARD_ARROW_DOWN} size={14} rotate={open && !closing ? '180deg' : '0'} />
            </Button>
          );
        }}
        contentClassName="flex-column margin-t-8 position-fixed"
        inheritRadius
        forcePosition="top-center"
        verticalOffset={-16}
        open={isSelectOpen}
        closeOnTabAway
      >
        <>
          <Container className="flex fd-column" height="168px">
            <Container
              className="padding-t-8"
              maxHeight="134px"
              style={{ overflowY: 'scroll', scrollbarWidth: 'thin' }}
            >
              {options.map((option) => {
                return (
                  <Button
                    key={option.value}
                    className="P2 radius-0 flex"
                    height="32px"
                    width="100%"
                    disabledOpacity={1}
                    hoverBackgroundColor="var(--gray-700)"
                    focusBackgroundColor="var(--gray-700)"
                    onClick={() => handleFontSelected(option)}
                  >
                    <Text clamp={1}>{option.label}</Text>
                  </Button>
                );
              })}
            </Container>

            <UploadNewFont afterUpload={afterUpload} onUploadStart={onUploadStart} />
          </Container>
        </>
      </Popover>
    </div>
  );
};

export default AddNewFontSelect;
